import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import Vue from "vue";
import VueI18n, { LocaleMessageObject } from "vue-i18n";
import deCountryCodeEnum from "../locales/country-code-enum/de.country-code-enum.json";
import enCountryCodeEnum from "../locales/country-code-enum/en.country-code-enum.json";
import de from "../locales/de.json";
import en from "../locales/en.json";

/**
 * Registered VueI18n for multiple languages.
 */
Vue.use(VueI18n);

/**
 * Currently supported locales.
 */
export const SUPPORT_LOCALES: LanguageCodeEnum[] = [
  LanguageCodeEnum.DE,
  LanguageCodeEnum.DE_CH,
  LanguageCodeEnum.EN,
  LanguageCodeEnum.FR,
  LanguageCodeEnum.PL,
  LanguageCodeEnum.RU
];

/**
 * Tracks which locales have already been loaded to avoid re-fetching.
 */
const loadedLocales = new Set<string>([LanguageCodeEnum.DE, LanguageCodeEnum.EN]);

/**
 * Dynamically imports a locale's main messages and country-code-enum messages.
 * Uses webpack magic comments for readable chunk names.
 */
async function importLocaleMessages(
  locale: string
): Promise<{ messages: LocaleMessageObject; countryCodeEnum: LocaleMessageObject }> {
  switch (locale) {
    case LanguageCodeEnum.DE_CH:
      return {
        messages: (await import(/* webpackChunkName: "locale-de_ch" */ "../locales/de_ch.json")).default,
        countryCodeEnum: (
          await import(
            /* webpackChunkName: "locale-de_ch" */ "../locales/country-code-enum/de_ch.country-code-enum.json"
          )
        ).default
      };
    case LanguageCodeEnum.FR:
      return {
        messages: (await import(/* webpackChunkName: "locale-fr" */ "../locales/fr.json")).default,
        countryCodeEnum: (
          await import(/* webpackChunkName: "locale-fr" */ "../locales/country-code-enum/fr.country-code-enum.json")
        ).default
      };
    case LanguageCodeEnum.PL:
      return {
        messages: (await import(/* webpackChunkName: "locale-pl" */ "../locales/pl.json")).default,
        countryCodeEnum: (
          await import(/* webpackChunkName: "locale-pl" */ "../locales/country-code-enum/pl.country-code-enum.json")
        ).default
      };
    case LanguageCodeEnum.RU:
      return {
        messages: (await import(/* webpackChunkName: "locale-ru" */ "../locales/ru.json")).default,
        countryCodeEnum: (
          await import(/* webpackChunkName: "locale-ru" */ "../locales/country-code-enum/ru.country-code-enum.json")
        ).default
      };
    default:
      throw new Error(`Unsupported locale: ${locale}`);
  }
}

/**
 * Loads a locale's messages on demand and sets it as the active locale.
 * Already-loaded locales are switched instantly without re-fetching.
 *
 * @param locale - The locale to load and activate
 * @returns The activated locale string
 */
export async function loadLocaleAsync(locale: string): Promise<string> {
  if (!SUPPORT_LOCALES.includes(locale as LanguageCodeEnum)) {
    console.warn(`Unsupported locale "${locale}", falling back to current locale "${i18n.locale}"`);
    return i18n.locale;
  }

  if (!loadedLocales.has(locale)) {
    const { messages, countryCodeEnum } = await importLocaleMessages(locale);
    i18n.setLocaleMessage(locale, { ...messages, ...countryCodeEnum });
    loadedLocales.add(locale);
  }

  i18n.locale = locale;
  return locale;
}

/**
 * Restores the locale from localStorage on app startup.
 * Call this early in the app entry point to ensure the correct locale is loaded
 * before the first render.
 */
export async function initLocaleFromStorage(): Promise<void> {
  try {
    const saved = localStorage.getItem("locale");
    if (saved && SUPPORT_LOCALES.includes(saved as LanguageCodeEnum) && !loadedLocales.has(saved)) {
      await loadLocaleAsync(saved);
    } else if (saved && loadedLocales.has(saved)) {
      i18n.locale = saved;
    }
  } catch {
    // localStorage may not be available; silently fall back to default
  }
}

/**
 * Exports the VueI18n plugin.
 * Only the default locale (de) and fallback locale (en) are loaded eagerly.
 * All other locales are loaded on demand via loadLocaleAsync().
 */
const i18n = new VueI18n({
  locale: process.env.VUE_APP_I18N_LOCALE || LanguageCodeEnum.DE,
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || LanguageCodeEnum.EN,
  messages: {
    en: {
      ...en,
      ...enCountryCodeEnum
    },
    de: {
      ...de,
      ...deCountryCodeEnum
    }
  }
});

export default i18n;
